/**
 * @file useComicDanmaku.js
 * @description 漫画阅读页 — 弹幕
 * @module features/comic/composables/useComicDanmaku
 */

import { computed } from 'vue'
import {
  fetchDanmakuConfApi,
  updateDanmakuConfApi,
  fetchDanmakuLaunchQueryApi,
  fetchDanmakuLaunchLikedApi,
  likeDanmakuLaunchApi,
  unlikeDanmakuLaunchApi,
  sendDanmakuLaunchApi,
} from '../api/comicApi'
import { ElMessage } from 'element-plus'

const DANMAKU_CONF_SESSION_KEY = 'danmaku_user_conf_json'
const DANMAKU_DISPLAY_AREA_PCT_MIN = 5
const DANMAKU_DISPLAY_AREA_PCT_MAX = 45

/**
 * @param {Record<string, Function>} bridge 共享方法桥
 * @param {object} ctx reactive 状态
 */
export function useComicDanmaku(bridge, ctx) {
  function bootstrapDanmakuUserConf() {
    try {
      const raw = sessionStorage.getItem(DANMAKU_CONF_SESSION_KEY)
      if (raw) {
        const parsed = JSON.parse(raw)
        if (parsed && typeof parsed === 'object') {
          applyDanmakuConfFromApi(parsed)
        }
      }
    } catch (e) {
      // ignore
    }
    if (ctx.token) {
      fetchDanmakuUserConfFromServer().catch(() => {})
    }
  }

  async function fetchDanmakuUserConfFromServer() {
    if (!ctx.token) return
    try {
      const response = await fetchDanmakuConfApi(ctx.token)
      const d = response.data || {}
      if (d.code !== 0 && d.code !== 200) {
        return
      }
      const conf = d.danmakuConf || d.data?.danmakuConf
      if (conf && typeof conf === 'object') {
        sessionStorage.setItem(DANMAKU_CONF_SESSION_KEY, JSON.stringify(conf))
        applyDanmakuConfFromApi(conf)
      }
    } catch (e) {
      console.warn('拉取弹幕默认配置失败:', e)
    }
  }

  function applyDanmakuConfFromApi(conf) {
    if (!conf || typeof conf !== 'object') return
    if (typeof conf.showDanmaku === 'boolean') {
      ctx.danmakuEnabled = conf.showDanmaku
    }
    const op = Number(conf.opacity)
    if (!Number.isNaN(op)) {
      ctx.danmakuTextOpacity = Math.min(1, Math.max(0.15, op / 100))
    }
    const area = Number(conf.displayArea)
    if (!Number.isNaN(area)) {
      ctx.danmakuDisplayAreaPercent = Math.min(
        DANMAKU_DISPLAY_AREA_PCT_MAX,
        Math.max(DANMAKU_DISPLAY_AREA_PCT_MIN, Math.round(area))
      )
    }
    const fs = Number(conf.fontSize)
    if ([1, 2, 3, 4].includes(fs)) {
      ctx.danmakuFontSizeEnum = fs
    }
    const sp = Number(conf.speed)
    if ([1, 2, 3, 4, 5].includes(sp)) {
      ctx.danmakuSpeedEnum = sp
    }
  }

  function danmakuFontEnumToPx(enumVal) {
    const map = { 1: 12, 2: 15, 3: 18, 4: 22 }
    const e = Number(enumVal)
    return map[e] || map[2]
  }

  function migratePxToFontEnum(px) {
    const opts = [
      { e: 1, px: 12 },
      { e: 2, px: 15 },
      { e: 3, px: 18 },
      { e: 4, px: 22 }
    ]
    const n = Number(px)
    let best = opts[1].e
    let diff = Infinity
    for (const o of opts) {
      const d = Math.abs(o.px - n)
      if (d < diff) {
        diff = d
        best = o.e
      }
    }
    return best
  }

  function danmakuSpeedEnumToDurationSec(enumVal) {
    const map = { 1: 45, 2: 38, 3: 28, 4: 18, 5: 12 }
    const e = Number(enumVal)
    const raw = map[e]
    const dur = raw != null ? raw : map[3]
    return Math.min(ctx.danmakuDurationMax, Math.max(ctx.danmakuDurationMin, dur))
  }

  function migrateDurationToSpeedEnum(sec) {
    const opts = [
      { e: 1, sec: 45 },
      { e: 2, sec: 38 },
      { e: 3, sec: 28 },
      { e: 4, sec: 18 },
      { e: 5, sec: 12 }
    ]
    const n = Number(sec)
    let best = opts[2].e
    let diff = Infinity
    for (const o of opts) {
      const d = Math.abs(o.sec - n)
      if (d < diff) {
        diff = d
        best = o.e
      }
    }
    return best
  }

  function danmakuFontSizeEnumLabel(e) {
    const map = { 1: '小', 2: '标准', 3: '大', 4: '超大' }
    return map[Number(e)] || map[2]
  }

  function danmakuSpeedEnumLabel(e) {
    const map = { 1: '超慢', 2: '慢', 3: '正常', 4: '快', 5: '超快' }
    return map[Number(e)] || map[3]
  }

  function danmakuFontSizeSliderTooltip(val) {
    return danmakuFontSizeEnumLabel(val)
  }

  function danmakuSpeedSliderTooltip(val) {
    return danmakuSpeedEnumLabel(val)
  }

  function danmakuDisplayAreaSliderTooltip(val) {
    const n = Number(val)
    return `${Number.isFinite(n) ? Math.round(n) : 0}% 屏高`
  }

  function buildDanmakuConfUpdatePayload() {
    return {
      opacity: Math.round(Math.min(100, Math.max(5, ctx.danmakuTextOpacity * 100))),
      displayArea: danmakuDisplayAreaPercentClamped.value,
      fontSize: ctx.danmakuFontSizeEnum,
      speed: ctx.danmakuSpeedEnum,
      showDanmaku: ctx.danmakuEnabled
    }
  }

  async function saveDanmakuUserConfRemote() {
    if (!ctx.token) {
      return false
    }
    try {
      const payload = buildDanmakuConfUpdatePayload()
      const response = await updateDanmakuConfApi(payload, ctx.token)
      const d = response.data || {}
      if (d.code !== 0 && d.code !== 200) {
        ElMessage.warning(d.msg || '弹幕配置同步失败')
        return false
      }
      sessionStorage.setItem(DANMAKU_CONF_SESSION_KEY, JSON.stringify(payload))
      return true
    } catch (e) {
      console.error('弹幕配置同步失败:', e)
      ElMessage.warning('弹幕配置同步失败')
      return false
    }
  }

  async function toggleDanmakuVisibilityFab() {
    ctx.danmakuEnabled = !ctx.danmakuEnabled
    await saveDanmakuUserConfRemote()
  }

  /**
   * 同步阅读页 chrome（卷工具条 + 弹幕 FAB）显示状态
   * @param {boolean} visible
   */
  function setReaderChromeVisible(visible) {
    ctx.showTopBar = visible
    ctx.danmakuFabVisible = visible
  }

  /**
   * 点击漫画空白区域：统一切换工具条与弹幕 FAB
   * @param {MouseEvent} ev
   */
  function onReaderChromeTapToggleDanmakuFab(ev) {
    const el = ev && ev.target
    if (!el || typeof el.closest !== 'function') {
      return
    }
    if (
      el.closest('.reader-danmaku-fab-stack') ||
      el.closest('.top-bar') ||
      el.closest('.catalog-overlay') ||
      el.closest('.reader-danmaku-interact-mask') ||
      el.closest('.reader-danmaku-interact-panel') ||
      el.closest('.reader-danmaku-bar') ||
      el.closest('.interact-area') ||
      el.closest('.restore-progress-overlay') ||
      el.closest('.load-volume-prompt') ||
      el.closest('.load-volume-indicator')
    ) {
      return
    }
    const tag = (el.tagName && el.tagName.toLowerCase()) || ''
    // 打赏/收藏等按钮已由 interact-area + button 兜底；链接等也不切换 FAB
    if (tag === 'button' || tag === 'a' || tag === 'input' || tag === 'textarea' || tag === 'select') {
      return
    }
    if (el.closest('button, a, input, textarea, select')) {
      return
    }
    const chromeVisible = ctx.showTopBar || ctx.danmakuFabVisible
    setReaderChromeVisible(!chromeVisible)
  }

  function onDanmakuSettingsOpened() {
    try {
      ctx._danmakuSettingsSnapshot = JSON.stringify({
        danmakuTextOpacity: ctx.danmakuTextOpacity,
        danmakuFontSizeEnum: ctx.danmakuFontSizeEnum,
        danmakuSpeedEnum: ctx.danmakuSpeedEnum,
        danmakuDisplayAreaPercent: danmakuDisplayAreaPercentClamped.value
      })
    } catch (e) {
      ctx._danmakuSettingsSnapshot = ''
    }
  }

  async function onDanmakuSettingsClosed() {
    saveDanmakuSettings()
    let cur = ''
    try {
      cur = JSON.stringify({
        danmakuTextOpacity: ctx.danmakuTextOpacity,
        danmakuFontSizeEnum: ctx.danmakuFontSizeEnum,
        danmakuSpeedEnum: ctx.danmakuSpeedEnum,
        danmakuDisplayAreaPercent: danmakuDisplayAreaPercentClamped.value
      })
    } catch (e) {
      cur = ''
    }
    const changed = !ctx._danmakuSettingsSnapshot || ctx._danmakuSettingsSnapshot !== cur
    if (!ctx.token || !changed) {
      return
    }
    await saveDanmakuUserConfRemote()
  }

  function loadDanmakuSettings() {
    try {
      const raw = localStorage.getItem('comic_reader_danmaku_settings_v1')
      if (!raw) return
      const o = JSON.parse(raw)
      if (typeof o.danmakuEnabled === 'boolean') ctx.danmakuEnabled = o.danmakuEnabled
      if (typeof o.danmakuTextOpacity === 'number') {
        ctx.danmakuTextOpacity = Math.min(1, Math.max(0.15, o.danmakuTextOpacity))
      }
      if ([1, 2, 3, 4, 5].includes(Number(o.danmakuSpeedEnum))) {
        ctx.danmakuSpeedEnum = Number(o.danmakuSpeedEnum)
      } else if (typeof o.danmakuScrollDurationSec === 'number') {
        ctx.danmakuSpeedEnum = migrateDurationToSpeedEnum(o.danmakuScrollDurationSec)
      }
      if ([1, 2, 3, 4].includes(Number(o.danmakuFontSizeEnum))) {
        ctx.danmakuFontSizeEnum = Number(o.danmakuFontSizeEnum)
      } else if (typeof o.danmakuFontSizePx === 'number') {
        ctx.danmakuFontSizeEnum = migratePxToFontEnum(o.danmakuFontSizePx)
      }
      if (
        typeof o.danmakuDisplayAreaPercent === 'number' &&
        Number.isFinite(o.danmakuDisplayAreaPercent)
      ) {
        ctx.danmakuDisplayAreaPercent = Math.min(
          DANMAKU_DISPLAY_AREA_PCT_MAX,
          Math.max(DANMAKU_DISPLAY_AREA_PCT_MIN, Math.round(o.danmakuDisplayAreaPercent))
        )
      } else if ([1, 2, 3].includes(Number(o.danmakuRowCount))) {
        ctx.danmakuDisplayAreaPercent = Math.min(
          DANMAKU_DISPLAY_AREA_PCT_MAX,
          Math.max(DANMAKU_DISPLAY_AREA_PCT_MIN, Number(o.danmakuRowCount) * 10)
        )
      }
    } catch (e) {
      // ignore
    }
  }

  function saveDanmakuSettings() {
    try {
      localStorage.setItem(
        'comic_reader_danmaku_settings_v1',
        JSON.stringify({
          danmakuEnabled: ctx.danmakuEnabled,
          danmakuTextOpacity: ctx.danmakuTextOpacity,
          danmakuFontSizeEnum: ctx.danmakuFontSizeEnum,
          danmakuSpeedEnum: ctx.danmakuSpeedEnum,
          danmakuDisplayAreaPercent: danmakuDisplayAreaPercentClamped.value
        })
      )
    } catch (e) {
      // ignore
    }
  }

  async function fetchDanmakuLaunchQuery() {
    if (!ctx.comicId || !ctx.volId) {
      return
    }
    closeDanmakuInteract()
    ctx.danmakuLaunchLoading = true
    try {
      let pageNum = 1
      let totalPages = 1
      const merged = []
      while (pageNum <= totalPages && pageNum <= 80) {
        const body =
          pageNum <= 1
            ? { comicId: ctx.comicId, volId: ctx.volId }
            : { comicId: ctx.comicId, volId: ctx.volId, pageNum }
        const response = await fetchDanmakuLaunchQueryApi(body, ctx.token)
        const d = response.data || {}
        if (d.code !== 0 && d.code !== 200) {
          throw new Error(d.msg || '弹幕加载失败')
        }
        const inner =
          d.data && typeof d.data === 'object' && !Array.isArray(d.data)
            ? { ...d, ...d.data }
            : d
        const tp =
          inner.pages ??
          inner.totalPages ??
          inner.totalPage ??
          inner.pageTotal ??
          inner.pagesTotal
        totalPages = Math.max(1, Number(tp) || 1)
        merged.push(...extractDanmakuLaunchPageContents(inner))
        pageNum += 1
      }
      applyDanmakuLaunchContents(merged)
    } catch (e) {
      console.error(e)
      ctx.danmakuList = []
      ElMessage.warning(e.message || '弹幕加载失败')
    } finally {
      ctx.danmakuLaunchLoading = false
      ctx.danmakuLaunchLoaded = true
    }
  }

  function extractDanmakuLaunchPageContents(inner) {
    if (!inner || typeof inner !== 'object') {
      return []
    }
    const raw =
      inner.contents ??
      inner.danmakuList ??
      inner.list ??
      inner.records ??
      inner.rows ??
      inner.dataList
    return Array.isArray(raw) ? raw : []
  }

  function normalizeDanmakuRowIdFromPayload(c, seqIndex) {
    const raw =
      c?.danmakuId ??
      c?.danmaku_id ??
      c?.id ??
      c?.danmakuID ??
      c?.danmaku_Id
    const trimmed =
      raw != null && String(raw).trim() !== '' ? String(raw).trim() : ''
    if (trimmed) {
      return trimmed
    }
    const tail = String(c?.content ?? '')
      .slice(0, 48)
      .replace(/\s+/g, ' ')
    return `__anon_${seqIndex}_${tail}`
  }

  function applyDanmakuLaunchContents(contents) {
    const rows = Array.isArray(contents) ? contents : []
    const seen = new Set()
    const deduped = []
    rows.forEach((c, idx) => {
      const id = normalizeDanmakuRowIdFromPayload(c, idx)
      if (seen.has(id)) {
        return
      }
      seen.add(id)
      deduped.push({ payload: c, id })
    })
    ctx.danmakuList = deduped.map(({ payload: c, id }) => ({
      id,
      text: c.content || '',
      highlight: false,
      colorHex: c.colorHex || '#FFFFFF',
      likeCount: Number(c.likeCount) || 0,
      isMine: !!(c.is_me ?? c.isMe),
      isBanner: false
    }))
  }

  function clearDanmakuInteractAutoCloseTimer() {
    if (ctx.danmakuInteractAutoCloseTimer != null) {
      clearTimeout(ctx.danmakuInteractAutoCloseTimer)
      ctx.danmakuInteractAutoCloseTimer = null
    }
  }

  function scheduleDanmakuInteractAutoClose() {
    clearDanmakuInteractAutoCloseTimer()
    ctx.danmakuInteractAutoCloseTimer = setTimeout(() => {
      ctx.danmakuInteractAutoCloseTimer = null
      closeDanmakuInteract()
    }, 5000)
  }

  function closeDanmakuInteract() {
    clearDanmakuInteractAutoCloseTimer()
    ctx.danmakuInteractOpen = false
    ctx.danmakuInteractDanmakuId = ''
    ctx.danmakuInteractContent = ''
    ctx.danmakuInteractLikeCount = 0
    ctx.danmakuInteractIsLiked = false
    ctx.danmakuInteractLikedLoading = false
  }

  async function onDanmakuSegmentClick(seg) {
    if (!seg || seg.isBanner || seg.noInteract || String(seg.id).startsWith('__')) {
      return
    }
    if (!ctx.token) {
      ctx.$emit('show-popup-message', '请您登录后操作', 'login-required')
      return
    }
    ctx.danmakuInteractDanmakuId = String(seg.id)
    ctx.danmakuInteractContent = seg.text || ''
    ctx.danmakuInteractLikeCount = typeof seg.likeCount === 'number' ? seg.likeCount : 0
    ctx.danmakuInteractOpen = true
    ctx.danmakuInteractIsLiked = false
    ctx.danmakuInteractLikedLoading = true
    try {
      const response = await fetchDanmakuLaunchLikedApi(ctx.danmakuInteractDanmakuId, ctx.token)
      const d = response.data || {}
      if (d.code !== 0 && d.code !== 200) {
        throw new Error(d.msg || '查询点赞状态失败')
      }
      const liked =
        typeof d.isLike === 'boolean'
          ? d.isLike
          : typeof d.data?.isLike === 'boolean'
            ? d.data.isLike
            : false
      ctx.danmakuInteractIsLiked = !!liked
    } catch (e) {
      console.error(e)
      ElMessage.warning(e.message || '查询点赞状态失败')
      closeDanmakuInteract()
    } finally {
      ctx.danmakuInteractLikedLoading = false
      if (ctx.danmakuInteractOpen) {
        scheduleDanmakuInteractAutoClose()
      }
    }
  }

  async function toggleDanmakuInteractLike() {
    if (!ctx.token || !ctx.danmakuInteractDanmakuId || ctx.danmakuInteractLikedLoading) {
      return
    }
    const id = ctx.danmakuInteractDanmakuId
    try {
      const response = ctx.danmakuInteractIsLiked
        ? await unlikeDanmakuLaunchApi(id, ctx.token)
        : await likeDanmakuLaunchApi(id, ctx.token)
      const d = response.data || {}
      if (d.code !== 0 && d.code !== 200) {
        ElMessage.warning(d.msg || '操作失败')
        return
      }
      const nextLiked = !ctx.danmakuInteractIsLiked
      const delta = nextLiked ? 1 : -1
      const nextCount = Math.max(0, (ctx.danmakuInteractLikeCount || 0) + delta)
      ctx.danmakuInteractIsLiked = nextLiked
      ctx.danmakuInteractLikeCount = nextCount
      const row = ctx.danmakuList.find((x) => String(x.id) === String(id))
      if (row) {
        row.likeCount = nextCount
      }
    } catch (e) {
      console.error(e)
      ElMessage.warning('点赞操作失败')
    }
  }

  function openDanmakuSendDialog() {
    if (!ctx.token) {
      ctx.$emit('show-popup-message', '请您登录后操作', 'login-required')
      return
    }
    if (!ctx.volId) {
      ElMessage.warning('卷信息加载中，请稍后再试')
      return
    }
    ctx.showDanmakuSendDialog = true
  }

  function danmakuSegmentsForTrack(lineIdx) {
    const items = danmakuDisplayItems.value
    const trackCount = danmakuRowIndices.value.length
    if (!items.length || trackCount <= 0) {
      return []
    }
    const laneItems = []
    for (let i = lineIdx; i < items.length; i += trackCount) {
      laneItems.push(items[i])
    }
    if (!laneItems.length) {
      return []
    }
    return laneItems.map((item, index) => ({
      ...item,
      _k: `${item.id}-L${lineIdx}-${index}`
    }))
  }

  function danmakuLineScrollStyle(lineIdx) {
    const base = danmakuScrollDurationSec.value
    const drift = lineIdx === 0 ? 0 : lineIdx === 1 ? 3.6 : -2.2
    let dur = base + drift * 0.45
    dur = Math.min(
      ctx.danmakuDurationMax,
      Math.max(ctx.danmakuDurationMin, dur)
    )
    const delaySec = -(lineIdx * 4.8)
    return {
      animationDuration: `${dur}s`,
      animationDelay: `${delaySec}s`
    }
  }

  function danmakuItemInlineStyle(seg) {
    const style = {
      fontSize: `${danmakuFontSizePx.value}px`,
      opacity: ctx.danmakuTextOpacity
    }
    if (
      seg.colorHex &&
      !seg.highlight &&
      !seg.isMine &&
      !seg.isBanner &&
      typeof seg.colorHex === 'string'
    ) {
      style.color = seg.colorHex
    }
    return style
  }

  async function submitDanmakuSend() {
    const text = (ctx.danmakuDraft || '').trim()
    if (!text) {
      ElMessage.warning('请输入弹幕内容')
      return
    }
    if (!ctx.token) {
      ctx.$emit('show-popup-message', '请您登录后操作', 'login-required')
      return
    }
    if (!ctx.volId) {
      ElMessage.warning('卷信息加载中，请稍后再试')
      return
    }
    const savedText = text
    ctx.showDanmakuSendDialog = false
    ctx.danmakuDraft = ''
    try {
      await new Promise((r) => setTimeout(r, 280))
    } catch (e) {
      // ignore
    }
    ctx.danmakuPendingFly = { text: savedText }

    await ctx.$nextTick()

    const barEl = ctx.$refs.danmakuBarRef
    let endX = window.innerWidth / 2
    let endY = window.innerHeight * 0.07
    if (barEl) {
      const r = barEl.getBoundingClientRect()
      if (r.width > 0 && r.height > 0) {
        endX = r.left + r.width / 2
        endY = r.top + r.height / 2
      }
    }

    const startX = window.innerWidth / 2
    const startY = window.innerHeight * 0.42
    const dx = endX - startX
    const dy = endY - startY

    ctx.danmakuFlyText = savedText
    ctx.danmakuFlyVars = {
      '--fly-dx': `${dx}px`,
      '--fly-dy': `${dy}px`
    }
    ctx.danmakuFlyActive = true
    ctx.danmakuFlyPlaying = false

    await ctx.$nextTick()
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        ctx.danmakuFlyPlaying = true
      })
    })
  }

  function onDanmakuFlyAnimationEnd(event) {
    if (event.target !== event.currentTarget) {
      return
    }
    const animName = String(event.animationName || '')
    if (!animName.includes('danmaku-fly-manga')) {
      return
    }
    const pending = ctx.danmakuPendingFly
    ctx.danmakuPendingFly = null
    ctx.danmakuFlyActive = false
    ctx.danmakuFlyPlaying = false
    if (!pending || pending.text == null) {
      return
    }
    danmakuAfterFlySendAndRefresh(pending.text)
  }

  async function danmakuAfterFlySendAndRefresh(savedText) {
    if (!ctx.token || !ctx.volId) {
      ElMessage.warning('发送失败：请先登录或等待卷加载完成')
      return
    }
    try {
      const response = await sendDanmakuLaunchApi(
        {
          comicId: ctx.comicId,
          volId: ctx.volId,
          content: savedText,
        },
        ctx.token
      )
      const d = response.data || {}
      if (d.code !== 0 && d.code !== 200) {
        ElMessage.warning(d.msg || '弹幕发送失败')
        return
      }
      await fetchDanmakuLaunchQuery()
      const highlightRow = [...ctx.danmakuList]
        .reverse()
        .find((row) => !row.isBanner && row.text === savedText)
      if (highlightRow) {
        highlightRow.highlight = true
        clearTimeout(ctx.danmakuHighlightClearTimer)
        ctx.danmakuHighlightClearTimer = setTimeout(() => {
          highlightRow.highlight = false
        }, 3200)
      }
    } catch (e) {
      console.error(e)
      ElMessage.warning(e.response?.data?.msg || '弹幕发送失败')
    }
  }

  const danmakuDisplayItems = computed(() => {
    if (!ctx.danmakuEnabled) {
      return []
    }
    return (ctx.danmakuList || []).map((row) => ({
      id: row.id,
      text: row.text,
      highlight: !!row.highlight,
      isMine: !!row.isMine,
      isPlaceholder: false,
      isBanner: !!row.isBanner,
      noInteract: !!row.noInteract,
      colorHex: row.colorHex || '',
      likeCount: typeof row.likeCount === 'number' ? row.likeCount : 0,
    }))
  })

  const danmakuFontSizePx = computed(() => danmakuFontEnumToPx(ctx.danmakuFontSizeEnum))

  const danmakuScrollDurationSec = computed(() => {
    const dur = danmakuSpeedEnumToDurationSec(ctx.danmakuSpeedEnum)
    return Math.min(ctx.danmakuDurationMax, Math.max(ctx.danmakuDurationMin, dur))
  })

  const danmakuDisplayAreaPercentClamped = computed(() => {
    const n = Number(ctx.danmakuDisplayAreaPercent)
    const base = Number.isFinite(n) ? n : 18
    return Math.min(
      DANMAKU_DISPLAY_AREA_PCT_MAX,
      Math.max(DANMAKU_DISPLAY_AREA_PCT_MIN, Math.round(base)),
    )
  })

  const danmakuRowCount = computed(() => {
    const p = danmakuDisplayAreaPercentClamped.value
    if (p < 13) return 1
    if (p < 24) return 2
    return 3
  })

  const danmakuConfiguredTrackCount = computed(() => {
    let n = Number(danmakuRowCount.value)
    if (![1, 2, 3].includes(n)) {
      n = 2
    }
    return n
  })

  const danmakuEffectiveTrackCount = computed(() => {
    const configured = danmakuConfiguredTrackCount.value
    const len = danmakuDisplayItems.value.length
    if (len === 0) {
      return configured
    }
    return Math.min(configured, len)
  })

  const danmakuBarInnerAreaStyle = computed(() => ({
    minHeight: `${danmakuDisplayAreaPercentClamped.value}vh`,
  }))

  const danmakuRowIndices = computed(() => {
    const n = danmakuEffectiveTrackCount.value
    return Array.from({ length: n }, (_, i) => i)
  })

  Object.assign(bridge, {
    bootstrapDanmakuUserConf,
    fetchDanmakuUserConfFromServer,
    applyDanmakuConfFromApi,
    danmakuFontEnumToPx,
    migratePxToFontEnum,
    danmakuSpeedEnumToDurationSec,
    migrateDurationToSpeedEnum,
    danmakuFontSizeEnumLabel,
    danmakuSpeedEnumLabel,
    danmakuFontSizeSliderTooltip,
    danmakuSpeedSliderTooltip,
    danmakuDisplayAreaSliderTooltip,
    buildDanmakuConfUpdatePayload,
    saveDanmakuUserConfRemote,
    toggleDanmakuVisibilityFab,
    onReaderChromeTapToggleDanmakuFab,
    onDanmakuSettingsOpened,
    onDanmakuSettingsClosed,
    loadDanmakuSettings,
    saveDanmakuSettings,
    fetchDanmakuLaunchQuery,
    extractDanmakuLaunchPageContents,
    normalizeDanmakuRowIdFromPayload,
    applyDanmakuLaunchContents,
    clearDanmakuInteractAutoCloseTimer,
    scheduleDanmakuInteractAutoClose,
    closeDanmakuInteract,
    onDanmakuSegmentClick,
    toggleDanmakuInteractLike,
    openDanmakuSendDialog,
    danmakuSegmentsForTrack,
    danmakuLineScrollStyle,
    danmakuItemInlineStyle,
    submitDanmakuSend,
    onDanmakuFlyAnimationEnd,
    danmakuAfterFlySendAndRefresh,
  })

  return {
    danmakuDisplayItems,
    danmakuFontSizePx,
    danmakuScrollDurationSec,
    danmakuDisplayAreaPercentClamped,
    danmakuRowCount,
    danmakuConfiguredTrackCount,
    danmakuEffectiveTrackCount,
    danmakuBarInnerAreaStyle,
    danmakuRowIndices,
  }
}
